import { useRef, useState } from "react";
import JobService from "../../services/JobService";
import { JobLanguage } from "../../lib/enums/jobs.enum";
import { Job } from "../../lib/types/job";
import { Evaluation } from "../../lib/types/evaluation";
import { useNavigate } from "react-router";
import { useGlobals } from "../../hooks/useGlobals";
import { getLanguageName } from "../../lib/config";
import { useTranslation } from "react-i18next";
import VoiceInput from "../../components/speech/VoiceInput";

function InterviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [jobDescription, setJobDescription] = useState<string>("");
  const [questions, setQuestions] = useState<Evaluation[]>([]);
  const [answers, setAnswers] = useState<any>({});
  const [feedback, setFeedback] = useState<any>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [evaluatingIndex, setEvaluatingIndex] = useState(null);
  const [error, setError] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    JobLanguage.UZ
  );
  const [feedbackLanguage, setFeedbackLanguage] = useState<string>(
    JobLanguage.UZ
  );
  const voiceInputRefs = useRef<{ [key: number]: any }>({});
  const textareaRefs = useRef<{ [key: number]: HTMLTextAreaElement | null }>(
    {}
  );

  const { authMember } = useGlobals();

  const handleGenerateQuestions = async () => {
    if (!authMember) {
      navigate("/login");
      return;
    }

    if (!jobDescription.trim()) {
      setError(t("interview.errors.emptyDescription"));
      return;
    }

    if (jobDescription.trim().length < 50) {
      setError(t("interview.errors.minLength"));
      return;
    }

    if (jobDescription.trim().length > 1500) {
      setError(t("interview.errors.maxLength"));
      return;
    }

    setError("");
    setIsGenerating(true);
    setQuestions([]);
    setAnswers({});
    setFeedback({});

    try {
      const jobService = new JobService();

      const result: Job = await jobService.analayz({
        description: jobDescription,
        language: selectedLanguage as JobLanguage,
      });

      if (result.evaluations) setQuestions(result.evaluations);
      if (result.language) setFeedbackLanguage(result.language);
    } catch (err) {
      setError(t("interview.errors.generateFailed"));
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAnswerChange = (index: any, value: any) => {
    setAnswers((prev: any) => ({
      ...prev,
      [index]: value,
    }));

    setTimeout(() => {
      const textarea = textareaRefs.current[index];
      if (textarea) {
        textarea.scrollTop = textarea.scrollHeight;
      }
    }, 0);
  };

  const handleSubmitAnswer = async (index: any, evaluation: Evaluation) => {
    if (!authMember) {
      navigate("/login");
      return;
    }

    if (voiceInputRefs.current[index]?.isRecording()) {
      voiceInputRefs.current[index]?.stopRecording();
    }

    const answer = answers[index];
    if (!answer?.trim()) {
      return;
    }

    if (!answer?.trim()) {
      setError(t("interview.errors.emptyAnswer"));
      return;
    }
    if (answer.length < 30) {
      setError(t("interview.errors.answerMinLength"));
      return;
    }
    if (answer.length > 1000) {
      setError(t("interview.errors.answerMaxLength"));
      return;
    }

    setEvaluatingIndex(index);

    try {
      const jobService = new JobService();

      const { feedback }: Evaluation = await jobService.evaluate(
        evaluation,
        answer,
        feedbackLanguage as JobLanguage
      );

      setFeedback((prev: any) => ({
        ...prev,
        [index]: feedback,
      }));
    } catch (err) {
      setError(t("interview.errors.evaluateFailed"));
      console.error(err);
    } finally {
      setEvaluatingIndex(null);
    }
  };

  const clearPage = () => {
    setQuestions([]);
    setJobDescription("");
    setAnswers({});
    setFeedback({});
    setError("");
  };

  const convertLanguageCode = (lang: string): string => {
    const languageMap: { [key: string]: string } = {
      [JobLanguage.UZ]: "uz-UZ",
      [JobLanguage.EN]: "en-US",
      [JobLanguage.KR]: "ko-KR",
    };
    return languageMap[lang] || "en-US";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-3">
            {t("interview.title")}
          </h1>
          <p className="text-lg text-gray-600">{t("interview.subtitle")}</p>
        </div>

        {/* Login bo'lmagan user uchun */}
        {!authMember && (
          <div className="bg-white rounded-2xl shadow-2xl p-12 text-center border border-gray-200">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-2xl mb-6">
              <svg
                className="w-10 h-10 text-indigo-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {t("interview.loginRequired")}
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              {t("interview.loginMessage")}
            </p>
            <button
              onClick={() => navigate("/login")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-10 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {t("interview.loginBtn")}
            </button>
          </div>
        )}

        {/* Login bo'lgan user uchun */}
        {authMember && (
          <>
            {/* Step 1 */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center font-bold text-white text-lg shadow-lg">
                    1
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {t("interview.step1Title")}
                  </h2>
                </div>
                <div className="flex items-center gap-3 bg-indigo-50 px-4 py-2.5 rounded-xl border border-indigo-200">
                  <span className="text-sm font-semibold text-gray-700">
                    {t("interview.language")}
                  </span>
                  <select
                    id="language"
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="text-sm px-3 py-1.5 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white font-semibold text-indigo-600 outline-none cursor-pointer hover:border-indigo-400 transition-colors"
                  >
                    <option value={JobLanguage.UZ}>
                      {t("interview.languageUz")}
                    </option>
                    <option value={JobLanguage.EN}>
                      {t("interview.languageEn")}
                    </option>
                    <option value={JobLanguage.KR}>
                      {t("interview.languageKr")}
                    </option>
                  </select>
                </div>
              </div>

              <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-xl mt-0.5">💡</span>
                  <p className="text-sm font-medium text-blue-900">
                    {t("interview.languageHint")}{" "}
                    <span className="font-bold">
                      {getLanguageName(selectedLanguage, t)}
                    </span>{" "}
                    {t("interview.languageHint2")}
                  </p>
                </div>
              </div>

              <textarea
                id="job-description"
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-base mb-3 transition-all hover:border-gray-400"
                placeholder={t("interview.jobDescPlaceholder")}
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                disabled={isGenerating}
                minLength={50}
                maxLength={1500}
              />
              <div className="text-right text-sm font-medium text-gray-500 mb-6">
                {jobDescription.length}/1500
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-center gap-3">
                  <span className="mt-0.5 text-lg">⚠️</span>
                  <span className="flex-1">{error}</span>
                  <button
                    onClick={() => setError("")}
                    className="ml-auto font-bold hover:text-red-900 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              )}

              <button
                onClick={
                  questions.length > 0 ? clearPage : handleGenerateQuestions
                }
                disabled={isGenerating}
                className={`w-full py-3 px-6 rounded-xl font-bold text-base transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02] ${
                  questions.length > 0
                    ? "bg-slate-200 hover:bg-slate-300 text-slate-900"
                    : "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white disabled:from-slate-400 disabled:to-slate-400"
                }`}
              >
                {isGenerating
                  ? "⏳ " + t("interview.generating")
                  : questions.length > 0
                  ? t("interview.startOver")
                  : t("interview.generateBtn")}
              </button>
            </div>

            {/* Step 2 */}
            {questions.length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center gap-4 mb-8 flex-wrap">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center font-bold text-white text-lg shadow-lg">
                    2
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {t("interview.step2Title")}
                  </h2>
                  <span className="text-sm bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-4 py-2 rounded-full ml-auto font-bold border border-indigo-200">
                    {Object.keys(feedback).length}/{questions.length}{" "}
                    {t("interview.completed")}
                  </span>
                </div>

                {questions.map((evaluation: Evaluation, index: number) => (
                  <div
                    key={index}
                    className={`bg-white rounded-2xl shadow-lg border-2 p-6 md:p-8 transition-all ${
                      feedback[index]
                        ? "border-green-300 bg-gradient-to-br from-green-50 to-emerald-50"
                        : "border-gray-200 hover:shadow-xl hover:border-indigo-200"
                    }`}
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <span className="shrink-0 w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                        {index + 1}
                      </span>
                      <p className="text-base md:text-lg font-bold text-gray-900 flex-1 pt-1">
                        {evaluation.question}
                      </p>
                    </div>

                    <>
                      <div className="relative">
                        <textarea
                          ref={(el: any) => (textareaRefs.current[index] = el)}
                          rows={6}
                          className="w-full px-4 py-3 pr-32 pb-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-base transition-all hover:border-gray-400"
                          placeholder={t("interview.answerPlaceholder")}
                          value={answers[index] || ""}
                          onChange={(e) =>
                            handleAnswerChange(index, e.target.value)
                          }
                          disabled={evaluatingIndex === index}
                          maxLength={1000}
                        />

                        {/* Voice button o'ng burchakda */}
                        <div className="absolute bottom-3 right-3">
                          <VoiceInput
                            ref={(el: any) =>
                              (voiceInputRefs.current[index] = el)
                            }
                            onResult={(text: string) =>
                              handleAnswerChange(index, text)
                            }
                            language={convertLanguageCode(selectedLanguage)}
                            onStopRecording={() => {
                              console.log(
                                "Recording stopped for question",
                                index
                              );
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 mb-4 text-sm font-medium text-gray-600">
                        <span>{t("interview.minCharacters")}</span>
                        <span>{(answers[index] || "").length}/1000</span>
                      </div>

                      <button
                        onClick={() => handleSubmitAnswer(index, evaluation)}
                        disabled={
                          !answers[index]?.trim() ||
                          (answers[index] || "").length < 30 ||
                          (answers[index] || "").length > 1000 ||
                          evaluatingIndex === index
                        }
                        className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-300 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:scale-100"
                      >
                        {evaluatingIndex === index
                          ? "⏳ " + t("interview.evaluating")
                          : t("interview.submitBtn")}
                      </button>
                    </>
                    {feedback[index] && (
                      <div className="mt-6 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-300 shadow-md">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <p className="font-bold text-green-700 text-base">
                            {t("interview.feedbackTitle")}
                          </p>
                        </div>
                        <p className="text-green-900 whitespace-pre-wrap leading-relaxed text-base">
                          {feedback[index]}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {questions.length === 0 &&
              !isGenerating &&
              jobDescription.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-indigo-100 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg">
                    <svg
                      className="w-10 h-10 text-indigo-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-xl font-bold text-gray-900">
                    {t("interview.emptyState")}
                  </p>
                  <p className="text-gray-600 mt-2">
                    {t("interview.emptyStateDesc")}
                  </p>
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
}

export default InterviewPage;
